import { jwtDecode } from "jwt-decode";
import { PageState, UnProtectedOperations } from "../../constants";
import type { JwtModel, TokenModel } from "../../types/authentication";
import { refreshAuthToken } from "../api/auth";

export const getTokens = () => ({
    accessToken: typeof localStorage !== "undefined" ? localStorage.getItem("candidate-hub-token") : null,
    refreshToken: typeof localStorage !== "undefined" ? localStorage.getItem("candidate-hub-refresh-token") : null,
});

export const getAccessToken = () => getTokens().accessToken;

export const storeTokens = (data?: Pick<TokenModel, "access_token" | "refresh_token">) => {
    if (typeof localStorage === "undefined") return;
    if (data == null) {
        localStorage.removeItem("candidate-hub-token");
        localStorage.removeItem("candidate-hub-refresh-token");
    } else {
        localStorage.setItem("candidate-hub-token", data.access_token);
        localStorage.setItem("candidate-hub-refresh-token", data.refresh_token);
    }
};

export const clearTokenFromStorage = () => storeTokens();

export const clearTokenAndNavigate = () => {
    clearTokenFromStorage();
    if (typeof window !== "undefined") {
        window.location.replace(`${window.location.origin}?state=${PageState.SESSION_EXPIRED}`);
    }
};

export const isTokenExpiring = (accessToken: string) => {
    try {
        const decodedToken = jwtDecode<JwtModel>(accessToken);
        if (!decodedToken || typeof decodedToken.exp !== "number") {
            return true;
        }
        const skewTime = new Date();
        skewTime.setMinutes(new Date().getMinutes() + 5);
        const expireTime = Math.floor(skewTime.getTime() / 1000.0);
        return decodedToken.exp <= expireTime;
    } catch {
        return true;
    }
};

let activeRefreshPromise: Promise<string | null> | null = null;

export const getValidAccessToken = async (): Promise<string | null> => {
    const { accessToken, refreshToken } = getTokens();
    if (!accessToken) return null;

    if (!isTokenExpiring(accessToken)) {
        return accessToken;
    }

    if (!refreshToken) {
        clearTokenAndNavigate();
        return null;
    }

    if (!activeRefreshPromise) {
        activeRefreshPromise = (async () => {
            try {
                const response = await refreshAuthToken(refreshToken);
                if (response && !response.isError && response.payload) {
                    storeTokens(response.payload);
                    return response.payload.access_token;
                }
                clearTokenAndNavigate();
                return null;
            } catch {
                clearTokenAndNavigate();
                return null;
            } finally {
                activeRefreshPromise = null;
            }
        })();
    }

    return activeRefreshPromise;
};

export const getCurrentUser = () => {
    const token = getAccessToken();
    if (token == null) return null;
    const decoded = jwtDecode<JwtModel>(token);
    return { email: decoded.email };
};

export const isLoggedIn = () => {
    const currentToken = getAccessToken();
    if (currentToken === null) return false;
    const decodedToken = jwtDecode<JwtModel>(currentToken);
    const now = new Date();
    const expireTime = Math.floor(now.getTime() / 1000.0);
    return decodedToken.exp > expireTime;
};

export const isProtected = (operationName: string | undefined): boolean => {
    if (operationName?.startsWith("_") === true) return false;

    let protectedOperation = true;
    switch (operationName) {
        case UnProtectedOperations.SIGNIN_EMAIL_PASSWORD:
        case UnProtectedOperations.SIGNUP_EMAIL_PASSWORD:
        case UnProtectedOperations.SIGNUP_EMAIL_TOKEN:
        case UnProtectedOperations.SIGNUP_EMAIL_RTS_TOKEN:
        case UnProtectedOperations.SIGNIN_FEDERATED_LOGIN:
        case UnProtectedOperations.VERIFY_EMAIL:
        case UnProtectedOperations.REFRESH_TOKEN:
        case UnProtectedOperations.INVITATION:
        case UnProtectedOperations.EXISTING_EMAIL:
        case UnProtectedOperations.RESET_PASSWORD:
        case UnProtectedOperations.SET_PASSWORD_BY_CODE:
        case UnProtectedOperations.SEND_BENEFITS_EMAIL:
        case UnProtectedOperations.UNSUBSCRIBE_REMINDER:
        case UnProtectedOperations.OPENED_DASHBOARD:
        case UnProtectedOperations.EXCHANGE_IMPERSONATE_TOKEN:
            protectedOperation = false;
            break;
        default:
            break;
    }
    return protectedOperation;
};
